// The languages the site speaks and the message format every translated string uses. Shared by
// the web app (apps/web/src/i18n), the API (apps/api/src/i18n.ts) and the check script
// (scripts/i18n.mjs). docs/I18N.md explains how strings are marked and translated.
//
// Messages are keyed by their English text (gettext style): the English sits in the code, and
// each language's catalog maps it to a translation. A message is a small subset of ICU
// MessageFormat:
//   {name}                     a value; a number comes out in the reader's format (1,234 · 1.234)
//   {name, number}             the same, said explicitly
//   {n, plural, one {# coin} other {# coins}}
//                              plural forms by the language's own rules (zero one two few many
//                              other, or exact =0 =1 …); # is the number in the reader's format
//   {kind, select, card {…} other {…}}
//                              one of several texts by a value
//   <name>…</name>  <name/>    markup the web app fills in (a link, bold text). Never in emails.
// Apostrophes are plain text (no ICU quoting). A literal { } can't appear in a message, nor a <
// that starts something tag-like.

export const LOCALES = [
  'en',
  'de',
  'es',
  'fr',
  'tr',
  'da',
  'ko',
  'ja',
  'it',
  'sv',
  'pl',
  'pt',
  'nl',
] as const;
export type Locale = (typeof LOCALES)[number];
export const DEFAULT_LOCALE: Locale = 'en';

export interface LocaleInfo {
  /** The language's name in itself, as the picker shows it. */
  native: string;
  english: string;
  /** flag-icons code for the picker. */
  flag: string;
  /** BCP 47 tag for Intl (numbers, dates, plural rules). */
  intl: string;
}

export const LOCALE_INFO: Record<Locale, LocaleInfo> = {
  en: { native: 'English', english: 'English', flag: 'gb', intl: 'en-US' },
  de: { native: 'Deutsch', english: 'German', flag: 'de', intl: 'de-DE' },
  es: { native: 'Español', english: 'Spanish', flag: 'es', intl: 'es-ES' },
  fr: { native: 'Français', english: 'French', flag: 'fr', intl: 'fr-FR' },
  tr: { native: 'Türkçe', english: 'Turkish', flag: 'tr', intl: 'tr-TR' },
  da: { native: 'Dansk', english: 'Danish', flag: 'dk', intl: 'da-DK' },
  ko: { native: '한국어', english: 'Korean', flag: 'kr', intl: 'ko-KR' },
  ja: { native: '日本語', english: 'Japanese', flag: 'jp', intl: 'ja-JP' },
  it: { native: 'Italiano', english: 'Italian', flag: 'it', intl: 'it-IT' },
  sv: { native: 'Svenska', english: 'Swedish', flag: 'se', intl: 'sv-SE' },
  pl: { native: 'Polski', english: 'Polish', flag: 'pl', intl: 'pl-PL' },
  pt: { native: 'Português', english: 'Portuguese', flag: 'br', intl: 'pt-BR' },
  nl: { native: 'Nederlands', english: 'Dutch', flag: 'nl', intl: 'nl-NL' },
};

export const isLocale = (x: unknown): x is Locale =>
  typeof x === 'string' && (LOCALES as readonly string[]).includes(x);

/**
 * The languages visitors get. A language is added here once its catalogs are complete and
 * reviewed (docs/I18N.md); until then its translations can be in the repo and deployed without
 * anyone seeing them: the site, the web Worker and the API treat any other language as English.
 */
export const LIVE_LOCALES: readonly Locale[] = [
  'en',
  // Reviewed by a native speaker (site, emails and server messages, Terms and Privacy Policy).
  'de',
  'es',
  'fr',
  'tr',
  'da',
  'ko',
  'ja',
  'it',
  'sv',
  'pl',
  'pt',
  'nl',
];

/** `locale` if it's live, else English. */
export const liveLocale = (locale: Locale): Locale =>
  LIVE_LOCALES.includes(locale) ? locale : DEFAULT_LOCALE;

/** The first supported language in a preference list ("de-AT", "pt_BR", "en"), or null. */
export function matchLocale(tags: readonly string[]): Locale | null {
  for (const tag of tags) {
    const primary = tag.trim().toLowerCase().split(/[-_]/)[0];
    if (isLocale(primary)) return primary;
  }
  return null;
}

/** An Accept-Language header's languages, most preferred first ("*" and q=0 left out). */
export function parseAcceptLanguage(header: string | null | undefined): string[] {
  if (!header) return [];
  return header
    .split(',')
    .slice(0, 20)
    .map((part, i) => {
      const [tag = '', ...params] = part.trim().split(';');
      const q = params.map((p) => /^\s*q=([\d.]+)\s*$/.exec(p)?.[1]).find(Boolean);
      return { tag: tag.trim(), q: q === undefined ? 1 : Number(q), i };
    })
    .filter((x) => x.tag && x.tag !== '*' && x.q > 0)
    .sort((a, b) => b.q - a.q || a.i - b.i)
    .map((x) => x.tag);
}

/** The cookie that holds a language the visitor picked (also read by the web Worker and the API). */
export const LOCALE_COOKIE = 'gp_lang';

/** The picked language in a Cookie header, or null. */
export function localeFromCookie(cookie: string | null | undefined): Locale | null {
  const m = new RegExp(`(?:^|;\\s*)${LOCALE_COOKIE}=([a-z]{2})(?:;|$)`).exec(cookie ?? '');
  return m && isLocale(m[1]) ? m[1] : null;
}

/** A request's language: ?lang=, then the picked one (cookie), then the browser's, then English. */
export function requestLocale(req: {
  lang?: string | null;
  cookie?: string | null;
  acceptLanguage?: string | null;
}): Locale {
  if (isLocale(req.lang)) return req.lang;
  return (
    localeFromCookie(req.cookie) ??
    matchLocale(parseAcceptLanguage(req.acceptLanguage)) ??
    DEFAULT_LOCALE
  );
}

/* ------------------------------------------------------------------ Catalogs */

/** One language's translations of one part of the site: English message → translation. */
export type Catalog = Readonly<Record<string, string>>;

/**
 * Marks English text for translation without translating it here: for strings defined once (a
 * list of labels) and translated where they're shown, with t(label). The check script finds them.
 */
export const msg = (message: string): string => message;

/** The catalog key of a message said in a context, for English that means two things ("Open"). */
export const contextKey = (context: string, message: string) => `[${context}] ${message}`;

/* ------------------------------------------------------------------ Message format */

type Node = string | Arg | Pound | Choice | Tag;
interface Arg {
  k: 'arg';
  name: string;
}
interface Pound {
  k: '#';
}
interface Choice {
  k: 'plural' | 'select';
  name: string;
  options: Record<string, Node[]>;
}
interface Tag {
  k: 'tag';
  name: string;
  /** null: a self-closing tag (<br/>). */
  children: Node[] | null;
}

export class MessageSyntaxError extends Error {}

const NAME = /[A-Za-z_][A-Za-z0-9_]*/y;
const SELECTOR = /=?[A-Za-z0-9_]+/y;
const OPEN_TAG = /<([a-z][a-z0-9]*)>/y;
const CLOSE_TAG = /<\/([a-z][a-z0-9]*)>/y;
const SELF_TAG = /<([a-z][a-z0-9]*)\/>/y;

function parseMessage(src: string): Node[] {
  let i = 0;
  const fail = (why: string): never => {
    throw new MessageSyntaxError(`${why} at ${i} in "${src}"`);
  };
  const sticky = (re: RegExp) => {
    re.lastIndex = i;
    const m = re.exec(src);
    if (m) i = re.lastIndex;
    return m;
  };
  const ws = () => {
    while (i < src.length && /\s/.test(src[i]!)) i++;
  };

  function nodes(end: '}' | `</${string}>` | null, inPlural: boolean): Node[] {
    const out: Node[] = [];
    let text = '';
    const flush = () => {
      if (text) out.push(text);
      text = '';
    };
    while (i < src.length) {
      const ch = src[i]!;
      if (ch === '{') {
        flush();
        out.push(argument(inPlural));
        continue;
      }
      if (ch === '}') {
        if (end !== '}') fail('unexpected }');
        flush();
        return out;
      }
      if (ch === '<') {
        const close = sticky(CLOSE_TAG);
        if (close) {
          if (end !== `</${close[1]}>`) fail(`unexpected </${close[1]}>`);
          flush();
          return out;
        }
        const self = sticky(SELF_TAG);
        if (self) {
          flush();
          out.push({ k: 'tag', name: self[1]!, children: null });
          continue;
        }
        const open = sticky(OPEN_TAG);
        if (open) {
          flush();
          out.push({ k: 'tag', name: open[1]!, children: nodes(`</${open[1]}>`, inPlural) });
          continue;
        }
      }
      if (ch === '#' && inPlural) {
        flush();
        out.push({ k: '#' });
        i++;
        continue;
      }
      text += ch;
      i++;
    }
    if (end !== null) fail(`missing ${end}`);
    flush();
    return out;
  }

  function argument(inPlural: boolean): Node {
    i++; // {
    ws();
    const name = sticky(NAME)?.[0] ?? fail('expected a name');
    ws();
    if (src[i] === '}') {
      i++;
      return { k: 'arg', name };
    }
    if (src[i] !== ',') fail('expected , or }');
    i++;
    ws();
    const type = sticky(NAME)?.[0];
    ws();
    if (type === 'number') {
      if (src[i] !== '}') fail('expected }');
      i++;
      return { k: 'arg', name };
    }
    if (type !== 'plural' && type !== 'select') return fail(`unknown type ${type ?? ''}`);
    if (src[i] !== ',') fail('expected ,');
    i++;
    const options: Record<string, Node[]> = {};
    for (;;) {
      ws();
      if (src[i] === '}') {
        i++;
        break;
      }
      const key = sticky(SELECTOR)?.[0] ?? fail('expected an option');
      ws();
      if (src[i] !== '{') fail('expected {');
      i++;
      options[key] = nodes('}', type === 'plural' || inPlural);
      i++; // }
    }
    if (!options.other) fail(`${type} needs an "other" option`);
    return { k: type, name, options };
  }

  return nodes(null, false);
}

const parsed = new Map<string, Node[]>();
function ast(message: string): Node[] {
  let a = parsed.get(message);
  if (!a) {
    a = parseMessage(message);
    if (parsed.size > 5000) parsed.clear();
    parsed.set(message, a);
  }
  return a;
}

/** Throws MessageSyntaxError when `message` isn't a valid message (the check script uses it). */
export function validateMessage(message: string) {
  parseMessage(message);
}

/** What a message uses, to compare a translation with its English: values and tags by name. */
export function messageShape(message: string): { args: string[]; tags: string[] } {
  const args = new Set<string>();
  const tags = new Set<string>();
  const walk = (ns: Node[]) => {
    for (const n of ns) {
      if (typeof n === 'string' || n.k === '#') continue;
      if (n.k === 'arg') args.add(n.name);
      else if (n.k === 'tag') {
        tags.add(n.name);
        if (n.children) walk(n.children);
      } else {
        args.add(n.name);
        for (const o of Object.values(n.options)) walk(o);
      }
    }
  };
  walk(parseMessage(message));
  return { args: [...args].sort(), tags: [...tags].sort() };
}

const numberFormats = new Map<string, Intl.NumberFormat>();
const pluralRules = new Map<string, Intl.PluralRules>();

/** A number in the language's own format. */
export function formatNumber(
  locale: Locale,
  n: number,
  options?: Intl.NumberFormatOptions,
): string {
  const key = options ? `${locale}|${JSON.stringify(options)}` : locale;
  let f = numberFormats.get(key);
  if (!f) numberFormats.set(key, (f = new Intl.NumberFormat(LOCALE_INFO[locale].intl, options)));
  return f.format(n);
}

function pluralCategory(locale: Locale, n: number): string {
  let r = pluralRules.get(locale);
  if (!r) pluralRules.set(locale, (r = new Intl.PluralRules(LOCALE_INFO[locale].intl)));
  return r.select(n);
}

export type MessageValues<T = never> = Record<string, string | number | T>;

/**
 * A message's parts with its values and tags filled in: strings, and whatever `tag` and the
 * values give (React nodes in the web app). A value is never read as markup.
 */
export function formatParts<T>(
  locale: Locale,
  message: string,
  values: MessageValues<T> = {},
  tag?: (name: string, children: (string | T)[] | null) => T,
): (string | T)[] {
  let nodes: Node[];
  try {
    nodes = ast(message);
  } catch {
    return [message];
  }
  const out: (string | T)[] = [];
  const push = (x: string | T) => {
    const last = out.length - 1;
    if (typeof x === 'string' && typeof out[last] === 'string') out[last] += x;
    else out.push(x);
  };
  const value = (name: string): string | T => {
    const v = values[name];
    if (v === undefined) return `{${name}}`;
    return typeof v === 'number' ? formatNumber(locale, v) : v;
  };
  const walk = (ns: Node[], pound: number | null, into: (x: string | T) => void) => {
    for (const n of ns) {
      if (typeof n === 'string') into(n);
      else if (n.k === '#') into(pound === null ? '#' : formatNumber(locale, pound));
      else if (n.k === 'arg') into(value(n.name));
      else if (n.k === 'tag') {
        if (!tag) {
          if (n.children) walk(n.children, pound, into);
        } else if (!n.children) into(tag(n.name, null));
        else {
          const inner: (string | T)[] = [];
          walk(n.children, pound, (x) => {
            const last = inner.length - 1;
            if (typeof x === 'string' && typeof inner[last] === 'string') inner[last] += x;
            else inner.push(x);
          });
          into(tag(n.name, inner));
        }
      } else if (n.k === 'plural') {
        const v = values[n.name];
        const num = typeof v === 'number' ? v : Number(v);
        const option =
          n.options[`=${num}`] ??
          n.options[Number.isFinite(num) ? pluralCategory(locale, num) : 'other'] ??
          n.options.other!;
        walk(option, Number.isFinite(num) ? num : null, into);
      } else {
        const v = values[n.name];
        walk(n.options[String(v)] ?? n.options.other!, pound, into);
      }
    }
  };
  walk(nodes, null, push);
  return out;
}

/** A message as plain text: values filled in, markup dropped (its words kept). */
export function formatMessage(
  locale: Locale,
  message: string,
  values: Record<string, string | number> = {},
): string {
  return formatParts<string>(locale, message, values).join('');
}

/** The translation of `message` (or of it in a context) from `catalog`, or the English. */
export function translate(
  catalog: Catalog | undefined,
  locale: Locale,
  message: string,
  values?: Record<string, string | number>,
  context?: string,
): string {
  const key = context ? contextKey(context, message) : message;
  return formatMessage(locale, catalog?.[key] ?? message, values);
}
